export interface CountUpOptions {
  readonly fromCents: number;
  readonly toCents: number;
  readonly durationMs: number;
  readonly reducedMotion?: boolean;
  readonly signal?: AbortSignal;
  readonly onUpdate: (valueCents: number) => void;
}

export function easedCentValue(fromCents: number, toCents: number, progress: number): number {
  const safeProgress = Math.max(0, Math.min(1, progress));
  const eased = 1 - (1 - safeProgress) ** 3;
  return Math.round(fromCents + (toCents - fromCents) * eased);
}

export function animateCentCount(options: CountUpOptions): Promise<void> {
  if (!Number.isSafeInteger(options.fromCents) || !Number.isSafeInteger(options.toCents)) {
    return Promise.reject(new RangeError('Count-up benötigt sichere Cent-Ganzzahlen.'));
  }
  if (options.reducedMotion === true || options.durationMs <= 0) {
    options.onUpdate(options.toCents);
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const startedAt = globalThis.performance?.now() ?? Date.now();
    const abort = (): void => reject(new DOMException('Count-up abgebrochen.', 'AbortError'));
    if (options.signal?.aborted === true) {
      abort();
      return;
    }
    options.signal?.addEventListener('abort', abort, { once: true });
    const tick = (time: number): void => {
      if (options.signal?.aborted === true) return;
      const progress = Math.min(1, (time - startedAt) / options.durationMs);
      options.onUpdate(easedCentValue(options.fromCents, options.toCents, progress));
      if (progress < 1) {
        globalThis.requestAnimationFrame(tick);
        return;
      }
      options.signal?.removeEventListener('abort', abort);
      options.onUpdate(options.toCents);
      resolve();
    };
    globalThis.requestAnimationFrame(tick);
  });
}
